import CloseIcon from "@mui/icons-material/Close";
import DateRangeIcon from "@mui/icons-material/DateRange";
import DevicesIcon from "@mui/icons-material/Devices";
import FilterListIcon from "@mui/icons-material/FilterList";
import GridViewIcon from "@mui/icons-material/GridView";

import LaptopIcon from "@mui/icons-material/Laptop";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SearchIcon from "@mui/icons-material/Search";
import TabletIcon from "@mui/icons-material/Tablet";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ExportCsvButton from "../../components/ExportCsvButton";
// import { useTheme as useAppTheme } from '../../style/theme';
import SubscriptionList from "./SubscriptionsCell";
import UserProfileModal from "./UserProfileModal";
import Pagination from "../../components/Pagination";
import RowPerPage from "../../components/RowPerPage";
import { useUsersPaginated, useUser } from "../../features/users/useUsers";
import api from "../../utilities/api";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import TableSkeleton from "../../components/TableSkeleton";
import CardSkeleton from "../../components/CardSkeleton";
import UserLink from "./UserLink";
import { Storage } from "../../components/Storage";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

const columns = [
  { label: "ID", key: "_id" },
  { label: "First Name", key: "firstName" },
  { label: "Last Name", key: "lastName" },
  { label: "Email", key: "email" },
  { label: "Verified", key: "isVerified" },
  { label: "Gender", key: "gender" },
  { label: "Role", key: "role" },
  { label: "Phone Number", key: "phoneNumber" },
  { label: "DOB", key: "dob" },
  { label: "Register IP", key: "registerIpAddress" },
  { label: "Auth Provider", key: "authProvider" },
  { label: "Created At", key: "createdAt" },
  { label: "Updated At", key: "updatedAt" },
  { label: "Profile Picture", key: "profilePicture" },
];

const DeviceIcon = ({ device = "" }) => {
  let IconComponent = DevicesIcon;

  if (device.includes("iOS")) {
    IconComponent = PhoneAndroidIcon;
  } else if (device.includes("Android") && device.includes("Tablet")) {
    IconComponent = TabletIcon;
  } else if (device.includes("Android")) {
    IconComponent = PhoneAndroidIcon;
  } else if (device.includes("Laptop")) {
    IconComponent = LaptopIcon;
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <IconComponent />
      <Typography variant="body2" color="text.secondary">
        {device || "- Device"}
      </Typography>
    </Box>
  );
};

function AdvancedExportDialog({
  open,
  onClose,
  total,
  query,
  users,
  analytics,
}) {
  const theme = useTheme();
  const [format, setFormat] = useState("csv");
  const [scope, setScope] = useState("page");
  const [includeAnalytics, setIncludeAnalytics] = useState(true);
  const [columns, setColumns] = useState([
    "_id",
    "firstName",
    "lastName",
    "email",
    "isVerified",
    "gender",
    "role",
    "phoneNumber",
    "dob",
    "registerIpAddress",
    "authProvider",
    "createdAt",
    "updatedAt",
    "profilePicture",
    "username",
  ]);

  const allColumns = [
    "_id",
    "firstName",
    "lastName",
    "username",
    "email",
    "recoveryEmail",
    "isVerified",
    "gender",
    "role",
    "accountType",
    "phoneNumber",
    "dob",
    "referred_by",
    "registerIpAddress",
    "authProvider",
    "firebaseUid",
    "profilePicture",
    "createdAt",
    "updatedAt",
  ];

  const toCsv = (rows, cols) => {
    const header = cols.join(",");
    const body = rows
      .map((r) =>
        cols
          .map((c) => {
            const v = r[c];
            if (Array.isArray(v)) return `"${v.join("|")}"`;
            if (v && typeof v === "object") {
              const json = JSON.stringify(v);
              return `"${json.replace(/"/g, '""')}"`;
            }
            const s = v == null ? "" : String(v).replace(/"/g, '""');
            return `"${s}"`;
          })
          .join(",")
      )
      .join("\n");
    return `${header}\n${body}`;
  };

  const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchAll = async () => {
    const { rowsPerPage, searchTerm, filters, sort } = query;
    const pageSize = 500;
    const pages = Math.ceil(total / pageSize);
    let all = [];
    for (let p = 0; p < pages; p++) {
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get("/api/user/users/paginated", {
        params: {
          page: p,
          limit: pageSize,
          search: searchTerm,
          role: filters?.role || undefined,
          gender: filters?.gender || undefined,
          verified:
            typeof filters?.verified === "boolean"
              ? String(filters.verified)
              : undefined,
          hasPhone:
            typeof filters?.hasPhone === "boolean"
              ? String(filters.hasPhone)
              : undefined,
          hasReferral:
            typeof filters?.hasReferral === "boolean"
              ? String(filters.hasReferral)
              : undefined,
          hasRegisterIp:
            typeof filters?.hasRegisterIp === "boolean"
              ? String(filters.hasRegisterIp)
              : undefined,
          authProvider: filters?.authProvider || undefined,
          createdFrom: filters?.createdFrom || undefined,
          createdTo: filters?.createdTo || undefined,
          updatedFrom: filters?.updatedFrom || undefined,
          updatedTo: filters?.updatedTo || undefined,
          sortField: sort?.field || "createdAt",
          sortOrder: sort?.order || "desc",
        },
      });
      all = all.concat(res.data.data.items || []);
    }
    return all;
  };

  const handleExport = async () => {
    const rows = scope === "page" ? users : await fetchAll();
    if (format === "json") {
      const out = JSON.stringify(
        { rows, analytics: includeAnalytics ? analytics : undefined },
        null,
        2
      );
      download(new Blob([out], { type: "application/json" }), "users.json");
      return;
    }
    let csv = toCsv(rows, columns);
    if (includeAnalytics && analytics) {
      const lines = [
        "\n# Analytics",
        `Total,${analytics.total}`,
        `Male,${analytics.gender?.male ?? 0}`,
        `Female,${analytics.gender?.female ?? 0}`,
        `Other,${analytics.gender?.other ?? 0}`,
        `Admin,${analytics.roles?.admin ?? 0}`,
        `User,${analytics.roles?.user ?? 0}`,
        `Phone With,${analytics.phoneNumber?.with ?? 0}`,
        `Phone Without,${analytics.phoneNumber?.without ?? 0}`,
        `Referral With,${analytics.referred_by?.with ?? 0}`,
        `Referral Without,${analytics.referred_by?.without ?? 0}`,
        `IP With,${analytics.registerIpAddress?.with ?? 0}`,
        `IP Without,${analytics.registerIpAddress?.without ?? 0}`,
        `Google,${analytics.authProvider?.google ?? 0}`,
        `Facebook,${analytics.authProvider?.facebook ?? 0}`,
        `Auth,${analytics.authProvider?.auth ?? 0}`,
      ];
      csv = `${csv}\n${lines.join("\n")}`;
    }
    download(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "users.csv");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Advanced Export</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Format"
              size="small"
              fullWidth
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Scope"
              size="small"
              fullWidth
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <MenuItem value="page">Current Page</MenuItem>
              <MenuItem value="all">All Filtered</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              select
              label="Columns"
              size="small"
              fullWidth
              SelectProps={{ multiple: true }}
              value={columns}
              onChange={(e) => setColumns(e.target.value)}
            >
              {allColumns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleExport} sx={{ mr: 1 }}>
              Export
            </Button>
            {/* <Button
              variant="text"
              onClick={() => setIncludeAnalytics((v) => !v)}
            >
              {includeAnalytics ? "Exclude Analytics" : "Include Analytics"}
            </Button> */}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

// Filter Dialog Component
const FilterDialog = ({ open, onClose, filterConfig, onFilterChange }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      props={{
        sx: {
          position: "absolute",
          bottom: 16,
          right: 16,
          width: "350px",
          maxWidth: "90vw",
          color: theme.palette.text.primary,
          borderRadius: 2,
          m: 0,
        },
      }}
      BackdropProps={
        {
          // sx: { backgroundColor: "rgba(0,0,0,0.3)" },
        }
      }
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6">Filter Tabs</Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {Object.keys(filterConfig).map((key) => (
          <Box key={key}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 2,
              }}
            >
              <Typography variant="body1">{key}</Typography>
              <Switch
                checked={filterConfig[key]}
                onChange={() => onFilterChange(key)}
                color="primary"
                size="small"
              />
            </Box>
            <Divider
            // sx={{ backgroundColor: alpha(theme.palette.divider, 0.5) }}
            />
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default function UserList() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState({
    role: "",
    gender: "",
    verified: undefined,
    hasPhone: undefined,
    hasReferral: undefined,
    hasRegisterIp: undefined,
    authProvider: [],
    createdFrom: "",
    createdTo: "",
    updatedFrom: "",
    updatedTo: "",
  });
  const [sort, setSort] = useState({ field: "createdAt", order: "desc" });
  const [advFilterOpen, setAdvFilterOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [livePresence, setLivePresence] = useState([]);

  useEffect(() => {
    let mounted = true;
    const loadPresence = async () => {
      try {
        const res = await api.get("/api/realtime-activity/live?windowSeconds=120&limit=500");
        if (mounted) setLivePresence(res.data?.data?.sessions || []);
      } catch (_) {}
    };
    loadPresence();
    const id = setInterval(loadPresence, 15000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const { data: listData = {}, isLoading } = useUsersPaginated({
    page,
    rowsPerPage,
    searchTerm,
    filters,
    sort,
  });
  const users = listData?.items ?? [];
  const total = listData?.total ?? 0;
  const onlineUserIds = new Set(livePresence.filter((x) => x.authenticated && x.user?._id).map((x) => String(x.user._id)));
  const authenticatedLiveUsers = livePresence.filter((x) => x.authenticated && x.user?._id);
  const signedInUsers = users.filter((u) => u.session === true || onlineUserIds.has(String(u._id)));
  const sortedUsers = [...users].sort((a, b) => {
    const ao = onlineUserIds.has(String(a._id)) ? 2 : a.session ? 1 : 0;
    const bo = onlineUserIds.has(String(b._id)) ? 2 : b.session ? 1 : 0;
    return bo - ao;
  });

  const params = new URLSearchParams(location.search);
  const urlUserId = params.get("userId");
  const urlTab = params.get("tab");
  const { data: userData } = useUser(urlUserId);

  // Deep linking: Open user profile if userId is in URL
  useEffect(() => {
    if (urlUserId) {
      // First try to find in current list
      const userFromList = users.find((u) => u._id === urlUserId || u.id === urlUserId);
      if (userFromList) {
        setSelectedUser(userFromList);
        setProfileModalOpen(true);
      } else if (userData) {
        // If not in list, use fetched user data
        setSelectedUser(userData);
        setProfileModalOpen(true);
      }
    }
  }, [urlUserId, users, userData]);

  const analytics = null;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const yearsAvailable = Array.from(
    new Set((analytics?.series?.createdMonthly || []).map((d) => d.year))
  );
  const currentYear = new Date().getFullYear();
  const defaultYear = yearsAvailable.includes(currentYear)
    ? currentYear
    : yearsAvailable[yearsAvailable.length - 1] || currentYear;
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const monthlyData = months.map((m, idx) => {
    const created =
      analytics?.series?.createdMonthly?.find(
        (d) => d.year === selectedYear && d.month === idx + 1
      )?.count || 0;
    const updated =
      analytics?.series?.updatedMonthly?.find(
        (d) => d.year === selectedYear && d.month === idx + 1
      )?.count || 0;
    return { month: m, created, updated };
  });
  const yearlyData = Array.from(
    new Set([
      ...(analytics?.series?.createdYearly || []).map((d) => d.year),
      ...(analytics?.series?.updatedYearly || []).map((d) => d.year),
    ])
  )
    .sort((a, b) => a - b)
    .map((y) => {
      const created =
        analytics?.series?.createdYearly?.find((d) => d.year === y)?.count || 0;
      const updated =
        analytics?.series?.updatedYearly?.find((d) => d.year === y)?.count || 0;
      return { year: String(y), created, updated };
    });
  const distColors = [
    "#1976d2",
    "#2e7d32",
    "#ed6c02",
    "#d32f2f",
    "#6d28d9",
    "#0288d1",
  ];

  // const imagePaths = [
  //   { src: "/images/karan11.png", width: 50, height: 50 },
  //   { src: "/images/karan12.png" }, // default size
  //   { src: "/images/karan13.png", width: 60, height: 60 },
  //   { src: "/images/karan7.png" }
  // ];
  // const { mode } = useAppTheme();

  // Define filter configuration with default values
  const [filterConfig, setFilterConfig] = useState({
    Client: true,
    Country: true,
    Email: true,
    "Registration Date": true,
    Device: true,
    "IP Address": true,
    Subscriptions: true,
    "Storage Usage": true,

    Status: true,
  });

  const handleViewChange = (_, newView) => {
    if (newView) setView(newView);
  };

  // NEW: handlePageChange accepts only newPage index
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleFilterDialogOpen = () => {
    setFilterDialogOpen(true);
  };

  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };

  const handleFilterChange = (key) => {
    setFilterConfig((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setProfileModalOpen(true);
  };

  const handleProfileModalClose = () => {
    setProfileModalOpen(false);
    // Remove userId from URL when closing modal
    const params = new URLSearchParams(location.search);
    if (params.has("userId")) {
      params.delete("userId");
      navigate({ search: params.toString() }, { replace: true });
    }
  };

  const paginated = sortedUsers;
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <>
      {/* <UserLink/> */}

      <Box
        sx={{
          // bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
          p: 3,
          minHeight: "100vh",
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(2),
          borderRadius: 2,
        }}
      >
        <Container maxWidth="">
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 2, sm: 0 },
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { sm: 4 },
                alignItems: { xs: "flex-start", sm: "center" },
              }}
            >
              <Typography variant="h4" fontWeight="bold">
                Users List
              </Typography>
              <RowPerPage
                rowsPerPage={rowsPerPage}
                onRowsChange={(n) => {
                  setRowsPerPage(n);
                  setPage(0);
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: { xs: "100%", sm: "auto" },
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                fullWidth
                placeholder="Search user"
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover fieldset": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />

              {/* <ExportCsvButton
                filename="view-details.csv"
                data={users}
                columns={columns}
              /> */}
              {/* Filter Button */}
              <ToggleButton
                value="filter"
                aria-label="filter view"
                onClick={handleFilterDialogOpen}
                sx={{
                  border: "none",
                  bgcolor: "transparent",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.action.hover, 0.1),
                  },
                }}
              >
                <FilterListIcon />
              </ToggleButton>

              <Button variant="outlined" onClick={() => setAdvFilterOpen(true)}>
                Filters
              </Button>
              {/* <Button variant="outlined" onClick={() => setExportOpen(true)}>
                Export
              </Button> */}

              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={handleViewChange}
                aria-label="view mode"
                size="small"
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                <ToggleButton value="list" sx={{ p: 2 }} aria-label="list view">
                  <ViewListIcon />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="grid view">
                  <GridViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Advanced Filters Dialog */}
          <Dialog
            open={advFilterOpen}
            onClose={() => setAdvFilterOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Filters</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Role"
                    fullWidth
                    size="small"
                    value={filters.role}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, role: e.target.value }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Gender"
                    fullWidth
                    size="small"
                    value={filters.gender}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, gender: e.target.value }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Verified"
                    fullWidth
                    size="small"
                    value={String(filters.verified ?? "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((f) => ({
                        ...f,
                        verified: v === "" ? undefined : v === "true",
                      }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">Verified</MenuItem>
                    <MenuItem value="false">Unverified</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Auth Provider"
                    fullWidth
                    size="small"
                    SelectProps={{ multiple: true }}
                    value={filters.authProvider}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((f) => ({ ...f, authProvider: v }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="google">Google</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="password">Auth</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Has Phone"
                    fullWidth
                    size="small"
                    value={String(filters.hasPhone ?? "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((f) => ({
                        ...f,
                        hasPhone: v === "" ? undefined : v === "true",
                      }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">With</MenuItem>
                    <MenuItem value="false">Without</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Has Referral"
                    fullWidth
                    size="small"
                    value={String(filters.hasReferral ?? "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((f) => ({
                        ...f,
                        hasReferral: v === "" ? undefined : v === "true",
                      }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">With</MenuItem>
                    <MenuItem value="false">Without</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Has Register IP"
                    fullWidth
                    size="small"
                    value={String(filters.hasRegisterIp ?? "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((f) => ({
                        ...f,
                        hasRegisterIp: v === "" ? undefined : v === "true",
                      }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">With</MenuItem>
                    <MenuItem value="false">Without</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Sort Field"
                    fullWidth
                    size="small"
                    value={sort.field}
                    onChange={(e) =>
                      setSort((s) => ({ ...s, field: e.target.value }))
                    }
                  >
                    <MenuItem value="createdAt">Created At</MenuItem>
                    <MenuItem value="updatedAt">Updated At</MenuItem>
                    <MenuItem value="firstName">First Name</MenuItem>
                    <MenuItem value="lastName">Last Name</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Sort Order"
                    fullWidth
                    size="small"
                    value={sort.order}
                    onChange={(e) =>
                      setSort((s) => ({ ...s, order: e.target.value }))
                    }
                  >
                    <MenuItem value="desc">Desc</MenuItem>
                    <MenuItem value="asc">Asc</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <DatePicker
                          label="Created From"
                          value={
                            filters.createdFrom
                              ? dayjs(filters.createdFrom)
                              : null
                          }
                          onChange={(v) => {
                            setFilters((f) => ({
                              ...f,
                              createdFrom: v ? v.toDate().toISOString() : "",
                            }));
                            setPage(0);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DatePicker
                          label="Created To"
                          value={
                            filters.createdTo ? dayjs(filters.createdTo) : null
                          }
                          onChange={(v) => {
                            setFilters((f) => ({
                              ...f,
                              createdTo: v ? v.toDate().toISOString() : "",
                            }));
                            setPage(0);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DatePicker
                          label="Updated From"
                          value={
                            filters.updatedFrom
                              ? dayjs(filters.updatedFrom)
                              : null
                          }
                          onChange={(v) => {
                            setFilters((f) => ({
                              ...f,
                              updatedFrom: v ? v.toDate().toISOString() : "",
                            }));
                            setPage(0);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DatePicker
                          label="Updated To"
                          value={
                            filters.updatedTo ? dayjs(filters.updatedTo) : null
                          }
                          onChange={(v) => {
                            setFilters((f) => ({
                              ...f,
                              updatedTo: v ? v.toDate().toISOString() : "",
                            }));
                            setPage(0);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>

          {false && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Total Users</Typography>
                    <Typography variant="h5">{analytics.total}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Gender</Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip label={`Male ${analytics.gender?.male ?? 0}`} />
                      <Chip label={`Female ${analytics.gender?.female ?? 0}`} />
                      <Chip label={`Other ${analytics.gender?.other ?? 0}`} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Roles</Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip label={`Admin ${analytics.roles?.admin ?? 0}`} />
                      <Chip label={`User ${analytics.roles?.user ?? 0}`} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Phone Numbers</Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip
                        label={`With ${analytics.phoneNumber?.with ?? 0}`}
                      />
                      <Chip
                        label={`Without ${analytics.phoneNumber?.without ?? 0}`}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Verification</Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip
                        label={`Verified ${analytics.verified?.verified ?? 0}`}
                      />
                      <Chip
                        label={`Unverified ${
                          analytics.verified?.unverified ?? 0
                        }`}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Referred By</Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip
                        label={`With ${analytics.referred_by?.with ?? 0}`}
                      />
                      <Chip
                        label={`Without ${analytics.referred_by?.without ?? 0}`}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Register IP</Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip
                        label={`With ${analytics.registerIpAddress?.with ?? 0}`}
                      />
                      <Chip
                        label={`Without ${
                          analytics.registerIpAddress?.without ?? 0
                        }`}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Auth Provider</Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip
                        label={`Google ${analytics.authProvider?.google ?? 0}`}
                      />
                      <Chip
                        label={`Facebook ${
                          analytics.authProvider?.facebook ?? 0
                        }`}
                      />
                      <Chip
                        label={`Auth ${analytics.authProvider?.auth ?? 0}`}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Created At</Typography>
                    <Box display="flex" gap={2} mt={1}>
                      <Chip
                        label={`Earliest ${
                          analytics.timestamps?.createdAt?.earliest
                            ? new Date(analytics.timestamps.createdAt.earliest)
                                .toISOString()
                                .split("T")[0]
                            : "-"
                        }`}
                      />
                      <Chip
                        label={`Latest ${
                          analytics.timestamps?.createdAt?.latest
                            ? new Date(analytics.timestamps.createdAt.latest)
                                .toISOString()
                                .split("T")[0]
                            : "-"
                        }`}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: "none",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2">Updated At</Typography>
                    <Box display="flex" gap={2} mt={1}>
                      <Chip
                        label={`Earliest ${
                          analytics.timestamps?.updatedAt?.earliest
                            ? new Date(analytics.timestamps.updatedAt.earliest)
                                .toISOString()
                                .split("T")[0]
                            : "-"
                        }`}
                      />
                      <Chip
                        label={`Latest ${
                          analytics.timestamps?.updatedAt?.latest
                            ? new Date(analytics.timestamps.updatedAt.latest)
                                .toISOString()
                                .split("T")[0]
                            : "-"
                        }`}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Year"
                      select
                      size="small"
                      fullWidth
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      {yearsAvailable.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Created/Updated by Month
                      </Typography>
                      <Box sx={{ height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monthlyData}>
                            <XAxis dataKey="month" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="created"
                              fill="#1976d2"
                              name="Created"
                            />
                            <Bar
                              dataKey="updated"
                              fill="#ed6c02"
                              name="Updated"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Created/Updated by Year
                      </Typography>
                      <Box sx={{ height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={yearlyData}>
                            <XAxis dataKey="year" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="created"
                              stroke="#1976d2"
                              name="Created"
                            />
                            <Line
                              type="monotone"
                              dataKey="updated"
                              stroke="#ed6c02"
                              name="Updated"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Gender</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              dataKey="value"
                              data={[
                                {
                                  name: "Male",
                                  value: analytics.gender?.male || 0,
                                },
                                {
                                  name: "Female",
                                  value: analytics.gender?.female || 0,
                                },
                                {
                                  name: "Other",
                                  value: analytics.gender?.other || 0,
                                },
                              ]}
                              outerRadius={80}
                              label
                            >
                              {[
                                analytics.gender?.male || 0,
                                analytics.gender?.female || 0,
                                analytics.gender?.other || 0,
                              ].map((_, i) => (
                                <Cell
                                  key={i}
                                  fill={distColors[i % distColors.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Verification</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                name: "Verified",
                                value: analytics.verified?.verified || 0,
                              },
                              {
                                name: "Unverified",
                                value: analytics.verified?.unverified || 0,
                              },
                            ]}
                          >
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar dataKey="value" fill="#2e7d32" name="Count" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Auth Provider</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                name: "Google",
                                value: analytics.authProvider?.google || 0,
                              },
                              {
                                name: "Facebook",
                                value: analytics.authProvider?.facebook || 0,
                              },
                              {
                                name: "Auth",
                                value: analytics.authProvider?.auth || 0,
                              },
                            ]}
                          >
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar dataKey="value" fill="#6d28d9" name="Count" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Phone/Referral/IP Presence
                      </Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                name: "Phone With",
                                value: analytics.phoneNumber?.with || 0,
                              },
                              {
                                name: "Phone Without",
                                value: analytics.phoneNumber?.without || 0,
                              },
                              {
                                name: "Referral With",
                                value: analytics.referred_by?.with || 0,
                              },
                              {
                                name: "Referral Without",
                                value: analytics.referred_by?.without || 0,
                              },
                              {
                                name: "IP With",
                                value: analytics.registerIpAddress?.with || 0,
                              },
                              {
                                name: "IP Without",
                                value:
                                  analytics.registerIpAddress?.without || 0,
                              },
                            ]}
                          >
                            <XAxis
                              dataKey="name"
                              interval={0}
                              angle={-20}
                              height={60}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar dataKey="value" fill="#0288d1" name="Count" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}

          {/* Filter Dialog */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <FilterDialog
              open={filterDialogOpen}
              onClose={handleFilterDialogClose}
              filterConfig={filterConfig}
              onFilterChange={handleFilterChange}
            />
          </Box>

          {/* User Profile Modal */}
          <UserProfileModal
            open={profileModalOpen}
            onClose={handleProfileModalClose}
            user={selectedUser}
            initialTab={urlTab}
          />

          {/* List or Grid - Now consider filterConfig */}
          <Box mb={3}>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ minWidth: 260, flex: 1 }}>
                <Typography fontWeight={700}>Online now: {authenticatedLiveUsers.length} · Signed in on this page: {signedInUsers.length}</Typography>
                <Typography variant="caption" color="text.secondary">Authenticated live users are surfaced here immediately. Green = activity within 2 minutes; Signed in = an active identity session.</Typography>
                {authenticatedLiveUsers.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                    {authenticatedLiveUsers.slice(0, 8).map((presence) => (
                      <Chip
                        key={presence.user._id}
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`${presence.user.firstName || presence.user.username || presence.user.email || "User"} · ${presence.property || "Clikkle"}`}
                        onClick={() => navigate(`/users?userId=${presence.user._id}`)}
                      />
                    ))}
                    {authenticatedLiveUsers.length > 8 && <Chip size="small" label={`+${authenticatedLiveUsers.length - 8} more`} onClick={() => navigate("/activity/live")} />}
                  </Box>
                )}
              </Box>
              <Button size="small" onClick={() => navigate("/activity/live")}>Open Activity Command View</Button>
            </Paper>
            {view === "list" ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                  padding: theme.spacing(2),
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: isDarkMode
                          ? "#171717"
                          : "background.neutral",
                      }}
                    >
                      {filterConfig["Client"] && <TableCell>Client</TableCell>}
                      {filterConfig["Country"] && (
                        <TableCell>Country</TableCell>
                      )}
                      {filterConfig["Email"] && <TableCell>Email</TableCell>}
                      {filterConfig["Registration Date"] && (
                        <TableCell>Registration Date</TableCell>
                      )}
                      {filterConfig["Device"] && <TableCell>Device</TableCell>}
                      {filterConfig["IP Address"] && (
                        <TableCell>IP Address</TableCell>
                      )}
                      {filterConfig["Subscriptions"] && (
                        <TableCell>Subscriptions</TableCell>
                      )}
                      {filterConfig["Storage Usage"] && (
                        <TableCell>Storage Usage</TableCell>
                      )}

                      {filterConfig["Status"] && <TableCell>Status</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {isLoading && paginated.length > 0 && (
                        <TableSkeleton
                          animation="wave"
                          variant="rectangular"
                          width="100%"
                          height={40}
                          sx={{
                            bgcolor: alpha(theme.palette.action.hover, 0.1),
                          }}
                        />
                      )}
                      {paginated.map((user) => (
                        <TableRow
                          key={user.id}
                          onClick={() => handleUserClick(user)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.action.hover, 0.1),
                            },
                          }}
                        >
                          {filterConfig["Client"] && (
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {user.profilePicture ? (
                                  <Box
                                    component="img"
                                    src={user.profilePicture}
                                    alt="Profile"
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <Avatar
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      bgcolor: theme.palette.primary.main,
                                      color: theme.palette.primary.contrastText,
                                    }}
                                  >
                                    {(user?.firstName?.[0] || "").toUpperCase()}
                                    {(user?.lastName?.[0] || "").toUpperCase()}
                                  </Avatar>
                                )}
                                <Box>
                                  <Typography>{user.firstName} {user.lastName}</Typography>
                                  {onlineUserIds.has(String(user._id)) ? <Chip size="small" color="success" label="Online now" sx={{ height: 18, fontSize: '0.62rem' }} /> : user.session === true ? <Chip size="small" variant="outlined" color="success" label="Signed in" sx={{ height: 18, fontSize: '0.62rem' }} /> : null}
                                </Box>
                                <Box display="flex" gap={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                                  {onlineUserIds.has(String(user._id)) && <Chip size="small" color="success" label="Online now" sx={{ height: 18, fontSize: '0.62rem' }} />}
                                  {!onlineUserIds.has(String(user._id)) && user.session === true && <Chip size="small" variant="outlined" color="success" label="Signed in" sx={{ height: 18, fontSize: '0.62rem' }} />}
                                </Box>
                              </Box>
                            </TableCell>
                          )}
                          {filterConfig["Country"] && (
                            <TableCell>{user?.country || "-"} </TableCell>
                          )}
                          {filterConfig["Email"] && (
                            <TableCell
                              sx={{
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                maxWidth: 200,
                              }}
                            >
                              {user.email}
                            </TableCell>
                          )}
                          {filterConfig["Registration Date"] && (
                            <TableCell>{user?.createdAt}</TableCell>
                          )}
                          {filterConfig["Device"] && (
                            <TableCell>
                              <DeviceIcon device={user?.device || "-"} />
                            </TableCell>
                          )}
                          {filterConfig["IP Address"] && (
                            <TableCell>
                              {user?.registerIpAddress
                                ? user.registerIpAddress === "::1"
                                  ? "DEVELOPMENT_MODE"
                                  : user.registerIpAddress.replace(
                                      /^::ffff:/,
                                      ""
                                    )
                                : "Not Provided"}
                            </TableCell>
                          )}
                          {filterConfig?.Subscriptions && (
                            <TableCell>
                              <SubscriptionList
                                subscriptions={user?.subscriptions}
                                total={user?.totalSubscriptions}
                              />
                            </TableCell>
                          )}
                          {filterConfig["Storage Usage"] && (
                            <TableCell>
                              <Storage user={user} />
                            </TableCell>
                          )}

                          {filterConfig["Status"] && (
                            <TableCell>
                              <Button
                                variant="contained"
                                // size="small"
                                sx={{
                                  bgcolor:
                                    user.isVerified === true
                                      ? "#42B82433"
                                      : "#B8242433",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#000",
                                }}
                              >
                                {(user.accountStatus && user.accountStatus !== "active")
                                  ? user.accountStatus.replace("_", " ")
                                  : user.isVerified === true ? "Active" : "Unverified"}
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </>
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {isLoading && (
                  <CardSkeleton
                    animation="wave"
                    variant="rectangular"
                    width="100%"
                    height={150}
                    sx={{ bgcolor: alpha(theme.palette.action.hover, 0.1) }}
                  />
                )}
                {paginated.map((user) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    // width={"450px"}
                    key={user.id}
                    sx={{
                      position: "relative",
                      overflow: "visible",
                    }}
                  >
                    <Card
                      sx={{
                        bgcolor: "transparent",
                        color: theme.palette.text.primary,
                        borderRadius: 2,
                        position: "relative",
                        overflow: "visible",
                        height: "100%",
                        cursor: "pointer",
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(2),
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: theme.shadows[8],
                        },
                      }}
                      onClick={() => handleUserClick(user)}
                    >
                      <CardContent sx={{ position: "relative", zIndex: 1 }}>
                        {filterConfig["Client"] && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={2}
                          >
                            <Box display="flex" gap={1} alignItems="center">
                              {user.profilePicture ? (
                                <Box
                                  component="img"
                                  src={user.profilePicture}
                                  alt="Profile"
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                  }}
                                >
                                  {(user?.firstName?.[0] || "").toUpperCase()}
                                  {(user?.lastName?.[0] || "").toUpperCase()}
                                </Avatar>
                              )}
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="medium"
                                >
                                  {user.firstName} {user.lastName}
                                </Typography>
                                {filterConfig["Email"] && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: "11rem",
                                    }}
                                    color="text.secondary"
                                  >
                                    {user.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            {filterConfig["Status"] && (
                              <Box display="flex" gap={1} alignItems="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      user.isVerified === true
                                        ? "#42B82433"
                                        : "#B8242433",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#fff"
                                        : "#000",
                                    boxShadow: "none",
                                    fontSize: '0.7rem',
                                    px: 1,
                                    py: 0.25
                                  }}
                                >
                                  {user.isVerified === true ? "Verified" : "Unverified"}
                                </Button>
                                {user?.accountStatus && user.accountStatus !== 'active' && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      bgcolor: ['suspended','banned','closed'].includes(user.accountStatus) ? 'error.light' : 'warning.light', 
                                      color: ['suspended','banned','closed'].includes(user.accountStatus) ? 'error.contrastText' : 'warning.contrastText',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1,
                                      fontSize: '0.7rem',
                                      textTransform: 'capitalize'
                                    }}
                                  >
                                    {user.accountStatus.replace('_', ' ')}
                                  </Typography>
                                )}
                                {user?.lastLogin && (
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontSize: '0.7rem' }}
                                  >
                                    Last seen: {new Date(user.lastLogin).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Show storage info prominently if available - matching popup format */}
                            {filterConfig["Storage Usage"] && user?.storage?.usedSpace && user?.storagePermission && (
                              <Box
                                sx={{
                                  bgcolor: theme.palette.action.hover,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {Math.round(user.storage.usedSpace / 1024 / 1024)}MB / {user.storagePermission.noOfAllowed}{user.storagePermission.format}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={(user.storage.usedSpace / (user.storagePermission.noOfAllowed * 1024 * 1024)) * 100}
                                  sx={{
                                    width: 40,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: (user.storage.usedSpace / (user.storagePermission.noOfAllowed * 1024 * 1024)) > 0.8 
                                        ? theme.palette.error.main 
                                        : (user.storage.usedSpace / (user.storagePermission.noOfAllowed * 1024 * 1024)) > 0.6 
                                        ? theme.palette.warning.main 
                                        : theme.palette.success.main,
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        )}

                        {(filterConfig["Country"] ||
                          filterConfig["IP Address"] ||
                          user?.role ||
                          user?.authProvider) && (
                          <Box
                            display="flex"
                            flexDirection="row"
                            gap={1.5}
                            mb={2}
                            flexWrap="wrap"
                          >
                            {filterConfig["Country"] && user?.code && (
                              <Typography fontSize={13} sx={{ bgcolor: 'action.hover', px: 1, borderRadius: 1 }}>
                                🌍 {user.code}
                              </Typography>
                            )}
                            {user?.role && (
                              <Typography fontSize={13} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 1, borderRadius: 1 }}>
                                {user.role}
                              </Typography>
                            )}
                            {user?.authProvider && (
                              <Typography fontSize={13} sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText', px: 1, borderRadius: 1 }}>
                                {Array.isArray(user.authProvider) ? user.authProvider.join(', ') : user.authProvider}
                              </Typography>
                            )}
                            {filterConfig["IP Address"] && (
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  {user?.registerIpAddress
                                    ? user.registerIpAddress === "::1"
                                      ? "DEVELOPMENT_MODE"
                                      : user.registerIpAddress.replace(
                                          /^::ffff:/,
                                          ""
                                        )
                                    : "Not Provided"}
                                </Typography>
                                {filterConfig["Device"] && user?.device && (
                                  <Typography fontSize={12} sx={{ bgcolor: 'action.selected', px: 1, borderRadius: 1 }}>
                                    📱 {user.device}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}

                        {filterConfig["Registration Date"] && (
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            gap={1}
                            mb={2}
                          >
                            <DateRangeIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {new Date(user?.createdAt).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({Math.ceil((Date.now() - new Date(user?.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days ago)
                            </Typography>
                          </Box>
                        )}

                        {filterConfig["Subscriptions"] && (
                          <Box
                            sx={{
                              mt: 2,
                              mb: 2,
                              zIndex: 10000,
                            }}
                          >
                            <SubscriptionList
                              subscriptions={user.subscriptions}
                            />
                          </Box>
                        )}

                        {filterConfig["Storage Usage"] && (
                          <Box>
                            <Storage user={user} />
                          </Box>
                        )}

                        {/* {filterConfig["Subscriptions"] && (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={2}
                          >
                            <Typography variant="body2" fontSize={11}>
                              Total Subscriptions
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {user.totalSubscriptions}
                            </Typography>
                          </Box>
                        )} */}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {/* Pagination */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing Rows: {page * rowsPerPage + 1}–
              {Math.min((page + 1) * rowsPerPage, total)} of {total}
            </Typography>

            {/* Corrected Pagination call */}
            <Pagination
              page={page}
              count={total}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Box>
        </Container>
      </Box>
      <AdvancedExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        total={total}
        query={{ rowsPerPage, searchTerm, filters, sort }}
        users={users}
        analytics={analytics}
      />
    </>
  );
}
